/**
 * Tenant Portal API Client
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.example.com/v1';

async function getToken() {
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

async function request(method, path, body) {
  const token = await getToken();
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(data?.error?.message || `HTTP ${resp.status}`);
    err.code = data?.error?.code;
    err.status = resp.status;
    throw err;
  }
  return data;
}

// Inventory
const listInventory = (params = {}) =>
  request('GET', `/inventory?${new URLSearchParams(params)}`);
const getInventoryItem = (itemId) => request('GET', `/inventory/${itemId}`);
const createInventoryItem = (data) => request('POST', '/inventory', data);
const updateInventoryItem = (itemId, data) => request('PUT', `/inventory/${itemId}`, data);
const deleteInventoryItem = (itemId) => request('DELETE', `/inventory/${itemId}`);
const updateItemStatus = (itemId, status) =>
  request('PUT', `/inventory/${itemId}/status`, { status });
const adjustInventory = (itemId, delta, reason) =>
  request('POST', `/inventory/${itemId}/adjust`, { delta, reason });
const buildBundle = (itemId, quantity, { unbuild = false } = {}) =>
  request('POST', `/inventory/${itemId}/build`, { quantity, unbuild });
const batchUpsertInventory = (items) => request('POST', '/inventory/batch', { items });
const getInventoryHistory = (itemId, params = {}) =>
  request('GET', `/inventory/${itemId}/history?${new URLSearchParams(params)}`);

// Inventory Categories
const listCategories = () => request('GET', '/inventory/categories');
const createCategory = (data) => request('POST', '/inventory/categories', data);
// categoryId may be a real CAT# id or, for item-only categories, the category
// name — encode it so names with spaces/punctuation route correctly.
const updateCategory = (categoryId, data) => request('PUT', `/inventory/categories/${encodeURIComponent(categoryId)}`, data);
const deleteCategory = (categoryId) => request('DELETE', `/inventory/categories/${encodeURIComponent(categoryId)}`);

// Warehouses
const listWarehouses = () => request('GET', '/warehouses');
const createWarehouse = (data) => request('POST', '/warehouses', data);
const listLocations = (warehouseId) => request('GET', `/warehouses/${warehouseId}/locations`);
const createLocation = (warehouseId, data) => request('POST', `/warehouses/${warehouseId}/locations`, data);
const createTransferOrder = (data) => request('POST', '/warehouses/transfer', data);

// Users
const listUsers = () => request('GET', '/users');
const inviteUser = (data) => request('POST', '/users', data);
const updateUser = (userId, data) => request('PUT', `/users/${userId}`, data);
const disableUser = (userId) => request('POST', `/users/${userId}/disable`);
const removeUser = (userId) => request('DELETE', `/users/${userId}`);

// API Keys
const listApiKeys = () => request('GET', '/api-keys');
const createApiKey = (data) => request('POST', '/api-keys', data);
const revokeApiKey = (keyId) => request('DELETE', `/api-keys/${keyId}`);
const rotateApiKey = (keyId) => request('PUT', `/api-keys/${keyId}/rotate`);

// Reports
const generateReport = (data) => request('POST', '/reports/generate', data);
const listReports = () => request('GET', '/reports');
const getReport = (reportId) => request('GET', `/reports/${reportId}`);
const createReportSchedule = (data) => request('POST', '/reports/schedule', data);

// Features (for nav visibility + self-service toggling)
const getMyFeatures = () => request('GET', '/me/features');
const updateMyFeature = (featureKey, enabled) =>
  request('PUT', `/me/features/${encodeURIComponent(featureKey)}`, { enabled });

// Usage
const getMyUsage = (period = '30d') => request('GET', `/me/usage?period=${period}`);

// Notifications
const getMyNotifications = () => request('GET', '/me/notifications');

// Profile / subscription
const getMyProfile = () => request('GET', '/me');
const updateMyProfile = (data) => request('PUT', '/me/profile', data);
const getAvatarUploadUrl = (contentType = 'image/jpeg') =>
  request('GET', `/me/avatar-upload-url?contentType=${encodeURIComponent(contentType)}`);

// Add-Ons (optional paid features)
const getAddOns    = ()                     => request('GET', '/tenant/addons');
const updateAddOn  = (addonId, data)        => request('PUT', `/tenant/addons/${addonId}`, data);

export const tenantApi = {
  // Inventory
  listInventory, getInventoryItem, createInventoryItem, updateInventoryItem,
  deleteInventoryItem, updateItemStatus, adjustInventory, buildBundle, batchUpsertInventory, getInventoryHistory,
  // Inventory Categories
  listCategories, createCategory, updateCategory, deleteCategory,
  // Warehouses
  listWarehouses, createWarehouse, listLocations, createLocation, createTransferOrder,
  // Users
  listUsers, inviteUser, updateUser, disableUser, removeUser,
  // API Keys
  listApiKeys, createApiKey, revokeApiKey, rotateApiKey,
  // Reports
  generateReport, listReports, getReport, createReportSchedule,
  // Add-Ons
  getAddOns, updateAddOn,
  // Meta
  getMyFeatures, updateMyFeature, getMyUsage, getMyProfile, updateMyProfile, getAvatarUploadUrl,
  // Notifications
  getMyNotifications,
};
